/**
 * Calculos de horario para la pantalla.
 *
 * Dos reglas que se aplican aca y que conviene tener presentes:
 *
 * 1. **El servidor es la autoridad.** Precio, disponibilidad y ventana los decide el
 *    backend. Lo unico que se calcula aca es a que hora local termina un turno, que es
 *    aritmetica de instantes con la zona del negocio: no es una regla de negocio
 *    duplicada, es como se muestra el dato que ya se eligio.
 * 2. **Nada de desfases fijos.** Toda conversion pasa por `packages/domain/src/tiempo/`,
 *    que es el unico modulo del monorepo que conoce `America/Montevideo` (ADR-038).
 *
 * Una reserva es instante + duracion (ADR-007): la de las 23:30 con 90 minutos termina
 * a la 01:00 del dia siguiente, y eso hay que **decirlo** en la pantalla.
 */

import {
  ZONA_HORARIA_NEGOCIO,
  denormalizarInicioLocal,
  fechaLocalDesdeInstante,
  instanteDesdeIso,
  localDesdeInstante,
  sumarDiasLocales,
  sumarMinutos,
  textoDeFechaLocal,
  textoDeHoraLocal,
  type DuracionMinutos,
} from '@padel/domain';

/** Final de un turno, ya expresado en hora local del negocio. */
export interface FinDeTurno {
  readonly localHora: string;
  readonly localFecha: string;
  readonly cruzaMedianoche: boolean;
}

/**
 * Calcula cuando termina un turno que empieza en `inicioUtc` y dura `duracion`.
 *
 * Devuelve `null` si el instante no es un ISO valido: la pantalla muestra un guion en
 * lugar de inventar una hora.
 */
export function finDeTurno(inicioUtc: string, duracion: DuracionMinutos): FinDeTurno | null {
  const inicio = instanteDesdeIso(inicioUtc);
  if (!inicio.ok) {
    return null;
  }

  const fin = localDesdeInstante(sumarMinutos(inicio.valor, duracion), ZONA_HORARIA_NEGOCIO);
  const { cruzaMedianoche } = denormalizarInicioLocal(inicio.valor, duracion, ZONA_HORARIA_NEGOCIO);

  return {
    localHora: textoDeHoraLocal(fin.hora),
    localFecha: textoDeFechaLocal(fin.fecha),
    cruzaMedianoche,
  };
}

/** Fecha local de hoy en `America/Montevideo`, en formato `YYYY-MM-DD`. */
export function hoyLocal(ahoraMs: number): string {
  const instante = instanteDesdeIso(new Date(ahoraMs).toISOString());
  if (!instante.ok) {
    // `toISOString` siempre produce un ISO con Z; este camino no deberia ocurrir nunca.
    return '';
  }
  return textoDeFechaLocal(fechaLocalDesdeInstante(instante.valor, ZONA_HORARIA_NEGOCIO));
}

/**
 * Dias que muestra el selector de fecha.
 *
 * Son `cantidadDias` dias locales consecutivos empezando por hoy, que es la forma de la
 * ventana publica de ADR-013. **La autoridad sigue siendo el servidor**: un dia que la
 * configuracion haya dejado fuera vuelve sin ningun inicio y la pantalla lo muestra
 * vacio, en vez de ofrecer algo que la callable va a rechazar.
 */
export function diasVisibles(ahoraMs: number, cantidadDias: number): readonly string[] {
  const hoy = hoyLocal(ahoraMs);
  const fecha = instanteDesdeIso(new Date(ahoraMs).toISOString());
  if (!fecha.ok || hoy === '') {
    return [];
  }

  const primera = fechaLocalDesdeInstante(fecha.valor, ZONA_HORARIA_NEGOCIO);
  const dias: string[] = [];
  for (let desplazamiento = 0; desplazamiento < cantidadDias; desplazamiento += 1) {
    dias.push(textoDeFechaLocal(sumarDiasLocales(primera, desplazamiento)));
  }
  return dias;
}
